export const isDomainLimitAvailable = (domainLimit) =>
    typeof domainLimit?.hasEffectivePackage === 'boolean'
    && Number.isInteger(domainLimit.effectiveMaxDomains)
    && (domainLimit.effectivePackageCode === null || typeof domainLimit.effectivePackageCode === 'string');

export const hasNoEffectiveSubscriptionPackage = (domainLimit) => domainLimit?.hasEffectivePackage === false;

export const isSingleDomainPackage = (domainLimit) =>
    hasNoEffectiveSubscriptionPackage(domainLimit) || domainLimit?.effectivePackageCode === 'free';

export const getMaximumDomains = (domainLimit) => {
    if (!isDomainLimitAvailable(domainLimit)) return null;

    return domainLimit.effectiveMaxDomains;
};

export const formatMaximumDomains = (maximumDomains) => {
    if (maximumDomains === null || maximumDomains === undefined) return '—';
    return maximumDomains <= 0 ? '∞' : maximumDomains;
};

export const groupDomainsByActivationAndHierarchy = (domains) => {
    const hostNameOf = ({projectDomain}) => projectDomain.hostName;
    const registrableDomainOf = ({registrableDomain}) => registrableDomain;
    const normalize = (hostName) => hostName?.toLocaleLowerCase('cs') ?? '';
    const domainNames = domains.map(hostNameOf);

    const listedParentOf = (hostName) => domainNames
        .filter((candidate) => candidate !== hostName && normalize(hostName).endsWith(`.${normalize(candidate)}`))
        .sort((left, right) => right.length - left.length)[0];

    const familyOf = (domain) => {
        const hostName = hostNameOf(domain);
        const registrableDomain = registrableDomainOf(domain);
        if (typeof registrableDomain === 'string' && registrableDomain !== '') return registrableDomain;

        // When the cache-only public-suffix lookup is unavailable, group only
        // by an explicitly listed hostname suffix; do not guess at TLD depth.
        const listedParent = listedParentOf(hostName);
        if (!listedParent) return hostName;

        const parentDomain = domains.find((candidate) => hostNameOf(candidate) === listedParent);
        const parentFamily = registrableDomainOf(parentDomain ?? {});
        return typeof parentFamily === 'string' && parentFamily !== '' ? parentFamily : listedParent;
    };

    const families = new Map();
    domains.forEach((domain, index) => {
        const family = familyOf(domain);
        if (!families.has(family)) families.set(family, []);
        families.get(family).push({domain, index});
    });

    const compareNames = (left, right) => hostNameOf(left).localeCompare(hostNameOf(right), 'cs', {sensitivity: 'base'});
    const orderedFamilies = [...families.entries()].sort(([leftName, leftMembers], [rightName, rightMembers]) => {
        const leftActive = leftMembers.some(({domain}) => domain.projectDomain.isEnabled === true);
        const rightActive = rightMembers.some(({domain}) => domain.projectDomain.isEnabled === true);
        return Number(rightActive) - Number(leftActive)
            || leftName.localeCompare(rightName, 'cs', {sensitivity: 'base'});
    });

    return orderedFamilies.map(([familyName, members]) => {
        const hasFamilyRoot = members.some(({domain}) => normalize(hostNameOf(domain)) === normalize(familyName));
        const orderedMembers = members.sort((left, right) => {
            const leftHost = hostNameOf(left.domain);
            const rightHost = hostNameOf(right.domain);
            const leftIsRoot = normalize(leftHost) === normalize(familyName)
                || (left.domain.isSubdomain === false && !hasFamilyRoot);
            const rightIsRoot = normalize(rightHost) === normalize(familyName)
                || (right.domain.isSubdomain === false && !hasFamilyRoot);
            return Number(rightIsRoot) - Number(leftIsRoot)
                || compareNames(left.domain, right.domain)
                || left.index - right.index;
        });
        return orderedMembers.map(({domain}) => domain);
    });
};

export const sortDomainsByActivationAndHierarchy = (domains) =>
    groupDomainsByActivationAndHierarchy(domains).flat();
